import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Star, RefreshCw, ChevronRight, CheckCircle2 } from 'lucide-react';
import RatingCriteriaDialog from '../../components/RatingCriteriaDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RatedStars = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} size={16} className={s <= rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'} />
    ))}
  </div>
);

export default function AuditorRatingsPanel() {
  const { getAuthHeader } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [needsAddress, setNeedsAddress] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogWorker, setDialogWorker] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openRatingDialog = (worker) => {
    setDialogWorker(worker);
    setDialogOpen(true);
  };

  const fetchToday = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ratings/today`, getAuthHeader());
      setWorkers(res.data.workers || []);
      setNeedsAddress(!!res.data.auditor_needs_address);
    } catch (e) {
      toast.error('Failed to load today\'s ratings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const ratedCount = workers.filter((w) => w.today_rated).length;
  const pct = workers.length ? Math.round((ratedCount / workers.length) * 100) : 0;

  return (
    <Card className="p-6 border-0 shadow-sm mb-8 ring-1 ring-amber-200" data-testid="auditor-ratings-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center">
            <Star size={22} className="text-amber-500 fill-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Daily Worker Ratings</h2>
            <p className="text-sm text-gray-500">
              Rate every worker active today — {ratedCount} of {workers.length} rated
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setLoading(true); fetchToday(); }}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            data-testid="ratings-panel-refresh-btn"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <Link
            to="/admin/ratings"
            className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-900"
            data-testid="ratings-panel-open-link"
          >
            Open Ratings <ChevronRight size={15} />
          </Link>
        </div>
      </div>

      {workers.length > 0 && (
        <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden" data-testid="ratings-progress-bar">
          <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Loading today's workers…</p>
        ) : needsAddress ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-3 text-center" data-testid="ratings-panel-needs-address">
            Your account has no home address on file. Ask an admin to add it in Staff Management — you can only rate workers within 70 miles of your address.
          </p>
        ) : workers.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center" data-testid="ratings-panel-empty">
            No workers in your 70-mile area have been active today yet. Check back once deliveries start.
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {workers.map((w) => (
              <div key={w.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`ratings-panel-worker-${w.id}`}>
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => openRatingDialog(w)}
                    className="font-medium text-gray-900 truncate hover:text-amber-700 hover:underline text-left"
                    data-testid={`ratings-panel-worker-name-${w.id}`}
                    title={w.today_rated ? 'View rating breakdown' : `Rate ${w.name}`}
                  >
                    {w.name}
                  </button>
                  <p className="text-xs text-gray-400">
                    Overall audit: {w.audit_rating != null ? `${w.audit_rating.toFixed(1)} ★` : 'not rated yet'}
                    {w.today_rated && w.today_rated_by && ` · today by ${w.today_rated_by}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {w.today_rated ? (
                    <button
                      type="button"
                      onClick={() => openRatingDialog(w)}
                      className="flex items-center gap-2"
                      title="View rating breakdown"
                      data-testid={`ratings-panel-rated-${w.id}`}
                    >
                      <RatedStars rating={w.today_rating} />
                      <CheckCircle2 size={16} className="text-green-500" data-testid={`ratings-panel-done-${w.id}`} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openRatingDialog(w)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                      data-testid={`ratings-panel-rate-btn-${w.id}`}
                    >
                      Rate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <RatingCriteriaDialog
        worker={dialogWorker}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        canEdit={true}
        onRated={fetchToday}
      />
    </Card>
  );
}
